import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { designRequestsAPI, usersAPI, clientsAPI } from '../../services/api';
import { Card, Button } from '../ui';
import Loading from '../common/Loading';
import { formatDate, handleApiError, showToast, showConfirm } from '../../utils/helpers';
import { PLATFORMS, CONTENT_TYPES } from '../../utils/platformConfig';
import { PlatformIcon } from '../icons/PlatformIcons';
import { 
  ArrowLeft, 
  Edit, 
  Trash2,
  UserPlus,
  RefreshCw,
  CheckCircle,
  XCircle,
  Palette,
  Building2,
  User,
  Calendar
} from 'lucide-react';

const DesignRequestDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin, isClient } = useAuth();
  const { isRTL, isArabic } = useLanguage();
  
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [users, setUsers] = useState([]);
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [converting, setConverting] = useState(false);
  
  // Edit mode
  const [editing, setEditing] = useState(false);
  const [editFormData, setEditFormData] = useState({
    description: '',
    status: '',
    assigned_designer_id: '',
    assigned_writer_id: '',
  });

  useEffect(() => {
    fetchRequestData();
    if (isAdmin) {
      fetchUsers();
    }
  }, [id, isAdmin]);

  const fetchRequestData = async () => {
    try {
      setLoading(true);
      const response = await designRequestsAPI.getById(id);
      setRequest(response.data.request);
      setEditFormData({
        description: response.data.request.description || '',
        status: response.data.request.status || 'pending',
        assigned_designer_id: response.data.request.assigned_designer_id || '',
        assigned_writer_id: response.data.request.assigned_writer_id || '',
      });
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await usersAPI.getAll();
      setUsers(response.data.users || []);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  const handleUpdate = async () => {
    try {
      setUpdating(true);
      const updateData = {};
      
      if (isClient) {
        // Clients can only update description
        if (editFormData.description !== request.description) {
          updateData.description = editFormData.description;
        }
      } else if (isAdmin) {
        // Admins can update everything
        if (editFormData.description !== request.description) {
          updateData.description = editFormData.description;
        }
        if (editFormData.status !== request.status) {
          updateData.status = editFormData.status;
        }
        if (editFormData.assigned_designer_id !== (request.assigned_designer_id || '')) {
          updateData.assigned_designer_id = editFormData.assigned_designer_id || null;
        }
        if (editFormData.assigned_writer_id !== (request.assigned_writer_id || '')) {
          updateData.assigned_writer_id = editFormData.assigned_writer_id || null;
        }
      }

      if (Object.keys(updateData).length === 0) {
        showToast.info(isRTL ? 'لا توجد تغييرات' : 'No changes to save');
        setEditing(false);
        return;
      }

      await designRequestsAPI.update(id, updateData);
      showToast.success(isRTL ? 'تم تحديث الطلب بنجاح' : 'Request updated successfully');
      await fetchRequestData();
      setEditing(false);
    } catch (err) {
      showToast.error(handleApiError(err));
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async () => {
    const confirmed = await showConfirm(
      isRTL 
        ? 'هل أنت متأكد من حذف هذا الطلب؟' 
        : 'Are you sure you want to delete this request?',
      isRTL
    );

    if (!confirmed) return;

    try {
      setDeleting(true);
      await designRequestsAPI.delete(id);
      showToast.success(isRTL ? 'تم حذف الطلب بنجاح' : 'Request deleted successfully');
      navigate('/design-requests');
    } catch (err) {
      showToast.error(handleApiError(err));
    } finally {
      setDeleting(false);
    }
  };

  const handleConvertToPost = async () => {
    const confirmed = await showConfirm(
      isRTL 
        ? 'هل تريد تحويل هذا الطلب إلى بوست؟ سيتم إنشاء بوست جديد بحالة "pending".' 
        : 'Do you want to convert this request to a post? A new post will be created with "pending" status.',
      isRTL
    );

    if (!confirmed) return;

    try {
      setConverting(true);
      const response = await designRequestsAPI.convertToPost(id);
      showToast.success(isRTL ? 'تم تحويل الطلب إلى بوست بنجاح' : 'Request converted to post successfully');
      navigate(`/posts/${response.data.post.id}`);
    } catch (err) {
      showToast.error(handleApiError(err));
    } finally {
      setConverting(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: isRTL ? 'قيد الانتظار' : 'Pending', color: 'bg-yellow-50 text-yellow-700 border border-yellow-100' },
      in_progress: { label: isRTL ? 'قيد التنفيذ' : 'In Progress', color: 'bg-blue-50 text-blue-700 border border-blue-100' },
      completed: { label: isRTL ? 'مكتمل' : 'Completed', color: 'bg-emerald-50 text-emerald-700 border border-emerald-100' },
      cancelled: { label: isRTL ? 'ملغي' : 'Cancelled', color: 'bg-rose-50 text-rose-700 border border-rose-100' },
    };

    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  if (loading) {
    return <Loading fullScreen />;
  }

  if (error || !request) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">{error || (isRTL ? 'الطلب غير موجود' : 'Request not found')}</p>
        <Button onClick={() => navigate('/design-requests')} variant="secondary">
          {isRTL ? 'العودة للقائمة' : 'Back to List'}
        </Button>
      </div>
    );
  }

  const platformInfo = request.platform ? PLATFORMS[request.platform] : null;
  const contentTypeInfo = request.content_type ? CONTENT_TYPES[request.content_type] : null;
  
  const designers = users.filter(u => u.role === 'designer');
  const writers = users.filter(u => u.role === 'writer');

  return (
    <div className="max-w-4xl mx-auto space-y-6" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <Card className="p-5 bg-gradient-to-r from-purple-50 via-white to-pink-50 border-slate-200">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-4 flex-1">
            <Link 
              to="/design-requests" 
              className="p-2 rounded-full hover:bg-white/70 transition-colors mt-1"
            >
              <ArrowLeft className={`h-5 w-5 text-slate-600 ${isRTL ? 'rotate-180' : ''}`} />
            </Link>
            <div className={isRTL ? 'text-right' : 'text-left'}>
              <div className="inline-flex items-center gap-2 mb-1">
                <Palette className="w-4 h-4 text-purple-600" />
                <span className="text-xs font-semibold uppercase tracking-wide text-purple-600">
                  {isRTL ? 'تفاصيل الطلب' : 'Request Details'}
                </span>
              </div>
              <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900">
                {isRTL ? 'تفاصيل طلب التصميم' : 'Design Request Details'}
              </h1>
              <div className="flex items-center gap-2 mt-2 text-sm text-slate-600">
                <Calendar className="w-4 h-4" />
                <span>{formatDate(request.created_at)}</span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            {!editing && (
              <>
                {(isAdmin || (isClient && request.status === 'pending')) && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setEditing(true)}
                  >
                    <Edit className={`h-4 w-4 ${isRTL ? 'mr-0 ml-2' : 'ml-0 mr-2'}`} />
                    {isRTL ? 'تعديل' : 'Edit'}
                  </Button>
                )}
                {isAdmin && request.status !== 'completed' && request.status !== 'cancelled' && (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleConvertToPost}
                    loading={converting}
                    disabled={converting}
                    className="bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600 text-white shadow-lg shadow-purple-500/30"
                  >
                    <RefreshCw className={`h-4 w-4 ${isRTL ? 'mr-0 ml-2' : 'ml-0 mr-2'}`} />
                    {isRTL ? 'تحويل إلى بوست' : 'Convert to Post'}
                  </Button>
                )}
                {(isAdmin || (isClient && request.status === 'pending')) && (
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={handleDelete}
                    loading={deleting}
                    disabled={deleting}
                  >
                    <Trash2 className={`h-4 w-4 ${isRTL ? 'mr-0 ml-2' : 'ml-0 mr-2'}`} />
                    {isRTL ? 'حذف' : 'Delete'}
                  </Button>
                )}
              </>
            )}
            {editing && (
              <>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleUpdate}
                  loading={updating}
                  disabled={updating}
                  className="bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600 text-white shadow-lg shadow-purple-500/30"
                >
                  <CheckCircle className={`h-4 w-4 ${isRTL ? 'mr-0 ml-2' : 'ml-0 mr-2'}`} />
                  {isRTL ? 'حفظ' : 'Save'}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setEditing(false);
                    setEditFormData({
                      description: request.description || '',
                      status: request.status || 'pending',
                      assigned_designer_id: request.assigned_designer_id || '',
                      assigned_writer_id: request.assigned_writer_id || '',
                    });
                  }}
                  disabled={updating}
                >
                  <XCircle className={`h-4 w-4 ${isRTL ? 'mr-0 ml-2' : 'ml-0 mr-2'}`} />
                  {isRTL ? 'إلغاء' : 'Cancel'}
                </Button>
              </>
            )}
          </div>
        </div>
      </Card>

      {/* Status Badge */}
      <div className={`flex items-center gap-2 ${isRTL ? 'justify-end' : 'justify-start'}`}>
        <span className="text-sm font-medium text-slate-700">
          {isRTL ? 'الحالة:' : 'Status:'}
        </span>
        {getStatusBadge(request.status)}
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          <Card className="p-6 rounded-2xl border border-slate-200 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <div className="p-2 rounded-lg bg-purple-50">
                <Palette className="w-5 h-5 text-purple-600" />
              </div>
              <h2 className="text-lg font-semibold text-slate-900">
                {isRTL ? 'الوصف' : 'Description'}
              </h2>
            </div>
            {editing ? (
              <textarea
                value={editFormData.description}
                onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none"
                rows="6"
                placeholder={isRTL ? 'أدخل وصف الطلب...' : 'Enter request description...'}
              />
            ) : (
              <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">
                {request.description || (isRTL ? 'لا يوجد وصف' : 'No description')}
              </p>
            )}
          </Card>

          {/* Platform & Content Type */}
          {(platformInfo || contentTypeInfo) && (
            <Card className="p-6 rounded-2xl border border-slate-200 shadow-sm">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">
                {isRTL ? 'المعلومات الفنية' : 'Technical Details'}
              </h2>
              <div className="space-y-4">
                {platformInfo && (
                  <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <label className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2 block">
                      {isRTL ? 'المنصة' : 'Platform'}
                    </label>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center justify-center w-10 h-10 rounded-full bg-white border border-slate-200">
                        <PlatformIcon platformId={platformInfo.id} className="w-5 h-5" />
                      </div>
                      <span className="text-base font-medium text-slate-900">
                        {isArabic ? platformInfo.nameAr : platformInfo.name}
                      </span>
                    </div>
                  </div>
                )}
                {contentTypeInfo && (
                  <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <label className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2 block">
                      {isRTL ? 'نوع المحتوى' : 'Content Type'}
                    </label>
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{contentTypeInfo.icon}</span>
                      <span className="text-base font-medium text-slate-900">
                        {isArabic ? contentTypeInfo.nameAr : contentTypeInfo.name}
                      </span>
                    </div>
                  </div>
                )}
                {request.specifications && Object.keys(request.specifications).length > 0 && (
                  <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <label className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2 block">
                      {isRTL ? 'المواصفات' : 'Specifications'}
                    </label>
                    <div className="mt-2 p-3 bg-white rounded border border-slate-200">
                      <pre className="text-sm text-slate-700 whitespace-pre-wrap font-mono">
                        {JSON.stringify(request.specifications, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            </Card>
          )}
        </div>

        {/* Right Column - Sidebar */}
        <div className="space-y-6">
          {/* Client Info */}
          <Card className="p-6 rounded-2xl border border-slate-200 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <div className="p-2 rounded-lg bg-sky-50">
                <Building2 className="w-5 h-5 text-sky-600" />
              </div>
              <h2 className="text-lg font-semibold text-slate-900">
                {isRTL ? 'العميل' : 'Client'}
              </h2>
            </div>
            <div className="space-y-3">
              <p className="font-semibold text-slate-900 text-base">{request.client_name}</p>
              {request.client_logo && (
                <img 
                  src={request.client_logo} 
                  alt={request.client_name}
                  className="w-20 h-20 rounded-xl object-cover border border-slate-200"
                />
              )}
            </div>
          </Card>

          {/* Assignments */}
          <Card className="p-6 rounded-2xl border border-slate-200 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <div className="p-2 rounded-lg bg-purple-50">
                <UserPlus className="w-5 h-5 text-purple-600" />
              </div>
              <h2 className="text-lg font-semibold text-slate-900">
                {isRTL ? 'التعيينات' : 'Assignments'}
              </h2>
            </div>
            <div className="space-y-4">
              {editing && isAdmin ? (
                <>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {isRTL ? 'المصمم' : 'Designer'}
                    </label>
                    <select
                      value={editFormData.assigned_designer_id}
                      onChange={(e) => setEditFormData({ ...editFormData, assigned_designer_id: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    >
                      <option value="">{isRTL ? '-- غير معين --' : '-- Not Assigned --'}</option>
                      {designers.map((designer) => (
                        <option key={designer.id} value={designer.id}>
                          {designer.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {isRTL ? 'الكاتب' : 'Writer'}
                    </label>
                    <select
                      value={editFormData.assigned_writer_id}
                      onChange={(e) => setEditFormData({ ...editFormData, assigned_writer_id: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    >
                      <option value="">{isRTL ? '-- غير معين --' : '-- Not Assigned --'}</option>
                      {writers.map((writer) => (
                        <option key={writer.id} value={writer.id}>
                          {writer.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {isRTL ? 'الحالة' : 'Status'}
                    </label>
                    <select
                      value={editFormData.status}
                      onChange={(e) => setEditFormData({ ...editFormData, status: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    >
                      <option value="pending">{isRTL ? 'قيد الانتظار' : 'Pending'}</option>
                      <option value="in_progress">{isRTL ? 'قيد التنفيذ' : 'In Progress'}</option>
                      <option value="completed">{isRTL ? 'مكتمل' : 'Completed'}</option>
                      <option value="cancelled">{isRTL ? 'ملغي' : 'Cancelled'}</option>
                    </select>
                  </div>
                </>
              ) : (
                <>
                  {request.designer_name && (
                    <div className="p-3 bg-purple-50 rounded-lg border border-purple-100">
                      <span className="text-xs font-medium text-purple-700 uppercase tracking-wide block mb-1">
                        🎨 {isRTL ? 'المصمم' : 'Designer'}
                      </span>
                      <p className="font-semibold text-slate-900">{request.designer_name}</p>
                      {request.designer_email && (
                        <p className="text-xs text-slate-500 mt-1">{request.designer_email}</p>
                      )}
                    </div>
                  )}
                  {request.writer_name && (
                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                      <span className="text-xs font-medium text-blue-700 uppercase tracking-wide block mb-1">
                        ✍️ {isRTL ? 'الكاتب' : 'Writer'}
                      </span>
                      <p className="font-semibold text-slate-900">{request.writer_name}</p>
                      {request.writer_email && (
                        <p className="text-xs text-slate-500 mt-1">{request.writer_email}</p>
                      )}
                    </div>
                  )}
                  {!request.designer_name && !request.writer_name && (
                    <p className="text-sm text-slate-500 italic text-center py-4">
                      {isRTL ? 'لا توجد تعيينات' : 'No assignments'}
                    </p>
                  )}
                </>
              )}
            </div>
          </Card>

          {/* Created By */}
          {request.created_by_name && (
            <Card className="p-6 rounded-2xl border border-slate-200 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-slate-50">
                  <User className="w-5 h-5 text-slate-600" />
                </div>
                <h2 className="text-lg font-semibold text-slate-900">
                  {isRTL ? 'معلومات الإنشاء' : 'Created By'}
                </h2>
              </div>
              <div>
                <p className="font-semibold text-slate-900">{request.created_by_name}</p>
                {request.created_by_email && (
                  <p className="text-sm text-slate-500 mt-1">{request.created_by_email}</p>
                )}
                <div className="flex items-center gap-2 mt-3 text-xs text-slate-500">
                  <Calendar className="w-3 h-3" />
                  <span>{formatDate(request.created_at)}</span>
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default DesignRequestDetailsPage;

